import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButton, type ActionPriority } from './ActionButton.js';
import { OnSurface } from './surface.js';

/**
 * Dialogs.
 *
 * `Dialog` is the general container; `ConfirmDialog` is the one that asks
 * before something is done that cannot simply be undone — deactivating a user,
 * cancelling a request, revoking a permission.
 *
 * Rules the confirmation enforces:
 *  - It states WHAT will happen, in the caller's own words, rather than a
 *    generic "Are you sure?".
 *  - Nothing is reported as done until the promise the caller returns has
 *    resolved. `isPending` disables both actions while it is in flight, so a
 *    double press cannot fire it twice, and the dialog closes only on success
 *    (CLAUDE.md section 8: a record never advances because a button was
 *    pressed).
 *  - The dismissing action is reachable first by keyboard, and Escape closes.
 *
 * Direction: nothing here positions by left or right. The footer uses
 * `justify-end`, which resolves to the trailing edge in both languages.
 */

export interface DialogProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  /** Already translated. */
  readonly title: string;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly size?: 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * The dialog's close button, named in the reader's language.
 *
 * HeroUI draws its own close button with a hard-coded English name ("Close"),
 * so under Arabic a screen reader announced English in the middle of an Arabic
 * dialog — found by the Phase 10 browser check. HeroUI clones the element given
 * as `closeButton` with its own props (press handling, focus ring, class and
 * that English label), so this takes every one of those props and replaces only
 * the name.
 *
 * EXPORTED because `Drawer` and a plain `Modal` (not wrapped by `Dialog` here)
 * take the identical `closeButton` prop and clone it the identical way — the
 * navigation drawer and `UserFormPage`'s activation dialog reuse this rather
 * than repeating it (browser check, K's defect 4).
 */
export function TranslatedCloseButton(props: ComponentPropsWithoutRef<'button'>) {
  const { t } = useTranslation();
  const { 'aria-label': _englishLabel, children: _unused, ...heroProps } = props;
  return (
    <button type="button" {...heroProps} aria-label={t('common.close')}>
      <svg aria-hidden="true" focusable="false" width="1em" height="1em" viewBox="0 0 24 24">
        <path
          d="M6 6l12 12M18 6L6 18"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}

export function Dialog({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
}: DialogProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      scrollBehavior="inside"
      // The heading is the accessible name; react-aria wires it to the dialog.
      aria-label={title}
      closeButton={<TranslatedCloseButton />}
    >
      <ModalContent>
        <ModalHeader className="text-section">{title}</ModalHeader>
        <ModalBody className="text-body">
          <OnSurface>{children}</OnSurface>
        </ModalBody>
        {footer ? <ModalFooter>{footer}</ModalFooter> : null}
      </ModalContent>
    </Modal>
  );
}

export interface ConfirmDialogProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  /** Already translated. */
  readonly title: string;
  /** What will actually happen, in the caller's words. Already translated. */
  readonly message: string;
  /** Label for the confirming action. Defaults to the generic confirm. */
  readonly confirmLabel?: string;
  readonly confirmPriority?: ActionPriority;
  readonly isPending?: boolean;
  /** Resolves when the server has done it. The dialog closes only then. */
  readonly onConfirm: () => void | Promise<void>;
  /** Anything extra the user should read before deciding. */
  readonly children?: ReactNode;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  title,
  message,
  confirmLabel,
  confirmPriority = 'destructive',
  isPending = false,
  onConfirm,
  children,
}: ConfirmDialogProps) {
  const { t } = useTranslation();

  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <ActionButton
            priority="tertiary"
            onPress={onClose}
            isDisabled={isPending}
          >
            {t('common.cancel')}
          </ActionButton>
          <ActionButton
            priority={confirmPriority}
            onPress={() => void handleConfirm()}
            isLoading={isPending}
          >
            {confirmLabel ?? t('common.confirm')}
          </ActionButton>
        </>
      }
    >
      <p>{message}</p>
      {children}
    </Dialog>
  );
}
